import { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface DynamicHeroProps {
  title: string | ReactNode;
  subtitle?: string;
  backgroundImage?: string;
  backgroundVideo?: string;
  overlay?: 'dark' | 'gradient' | 'glass';
  height?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  children?: ReactNode;
  className?: string;
}

export function DynamicHero({
  title,
  subtitle,
  backgroundImage,
  backgroundVideo,
  overlay = 'gradient',
  height = 'lg',
  children,
  className
}: DynamicHeroProps) {
  const heightClasses = {
    sm: 'min-h-[40vh]',
    md: 'min-h-[60vh]',
    lg: 'min-h-[80vh]',
    xl: 'min-h-[90vh]',
    full: 'min-h-screen'
  };

  const overlayClasses = {
    dark: 'bg-black/60',
    gradient: 'bg-gradient-to-t from-background via-background/80 to-transparent',
    glass: 'glass'
  };

  return (
    <div className={cn(
      "relative w-full overflow-hidden",
      heightClasses[height],
      className
    )}>
      {/* Background Media */}
      <div className="absolute inset-0 z-0">
        {backgroundVideo ? (
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover animate-blur-in"
          >
            <source src={backgroundVideo} type="video/mp4" />
          </video>
        ) : backgroundImage ? (
          <div
            className="w-full h-full bg-cover bg-center animate-blur-in scale-110 transition-transform duration-[20s] ease-out hover:scale-100"
            style={{ backgroundImage: `url(${backgroundImage})` }}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-background via-background/95 to-primary/10" />
        )}
        
        {/* Overlay */}
        <div className={cn("absolute inset-0", overlayClasses[overlay])} />
        
        {/* Animated Gradient Accent */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10 animate-shimmer opacity-30" 
             style={{ backgroundSize: '200% 200%' }} />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 h-full flex flex-col justify-center items-center text-center">
        <div className="max-w-4xl space-y-6 animate-fade-in-up">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight">
            <span className="block mb-2">{title}</span>
          </h1>
          
          {subtitle && (
            <p className="text-lg md:text-2xl text-muted-foreground max-w-2xl mx-auto animate-fade-in-up" 
               style={{ animationDelay: '0.1s' }}>
              {subtitle}
            </p>
          )}
          
          {children && (
            <div className="pt-4 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              {children}
            </div>
          )}
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-foreground/30 rounded-full flex items-start justify-center p-2">
          <div className="w-1.5 h-3 bg-foreground/30 rounded-full animate-pulse" />
        </div>
      </div>
    </div>
  );
}
